/**
 * Conversioni tra ciò che l'operatore scrive e ciò che va al backend.
 *
 * Le percentuali nel database sono FRAZIONI (0.06 = 6%). In interfaccia si
 * scrivono come percentuali, e la conversione avviene solo qui.
 */

const DECIMAL_RE = /^\d+([.,]\d+)?$/;

/**
 * Una percentuale scritta a mano, letta in TRE modi che non si confondono:
 * un valore, un campo lasciato vuoto, o un errore.
 *
 * Prima c'era un solo `null` per «vuoto» e per «non capito», e il chiamante non
 * poteva distinguerli: `6%` o `abc` diventavano `null`, cioè un campo
 * facoltativo svuotato senza una parola (o, in una modifica, un valore che
 * c'era già cancellato).
 *
 *  - `6`, `6,5`, ` 6 `, `6%`, `6 %` -> valore (frazione: "0.06", "0.065");
 *  - vuoto, e **anche `0`**: zero non è una percentuale, è «non dichiarato». Nel
 *    catalogo una regola che la prop non ha (consistenza, drawdown
 *    giornaliero…) si lascia vuota o si scrive 0, ed è la stessa cosa. Il
 *    backend rifiuta uno zero (`fraction`: 0 < x <= 1) perché un valore
 *    configurato a zero è quasi sempre un campo dimenticato, quindi da qui non
 *    parte mai;
 *  - il resto -> errore, col motivo. Sopra il 100% non è un dato ma un'unità
 *    sbagliata: una percentuale sopra 100 diventa una frazione sopra 1, cioè un
 *    drawdown del mille per cento che non brucia mai il conto, e niente lo
 *    segnalerebbe.
 */
export type PctParse = { kind: "empty" } | { kind: "value"; value: string } | { kind: "error"; message: string };

export function parsePct(input: string): PctParse {
  if (input.trim() === "") return { kind: "empty" };
  const v = input.trim().replace(/\s*%$/, "").trim().replace(",", ".");
  // Un «%» da solo non è un campo vuoto: è qualcosa che non si è capito.
  if (!DECIMAL_RE.test(v)) return { kind: "error", message: "Scrivi un numero, per esempio 6 (anche 6%)" };
  const n = Number(v);
  if (n === 0) return { kind: "empty" };
  if (n > 100) return { kind: "error", message: `${n}% non è una percentuale valida: sopra il 100% è un errore di unità, non un dato` };
  return { kind: "value", value: String(Math.round((n / 100) * 1_000_000) / 1_000_000) };
}

/** "6" o "6,5" o "6%" -> "0.06" / "0.065". Vuoto, zero o non valido -> null: per distinguerli, `parsePct`. */
export function pctToFraction(input: string): string | null {
  const p = parsePct(input);
  return p.kind === "value" ? p.value : null;
}

/** Il motivo per cui un campo percentuale NON si può salvare, col suo nome; null se va bene (anche vuoto). */
export function pctIssue(label: string, input: string): string | null {
  const p = parsePct(input);
  return p.kind === "error" ? `${label}: ${p.message}` : null;
}

/** "0.060000" -> "6". */
export function fractionToPct(fraction: string | null | undefined): string {
  if (fraction === null || fraction === undefined || fraction === "") return "";
  return String(Math.round(Number(fraction) * 100 * 10_000) / 10_000);
}

/** Numero decimale scritto dall'operatore, con la virgola o col punto. */
export function decimalInput(input: string): string | null {
  const v = input.trim().replace(",", ".");
  return DECIMAL_RE.test(v) ? v : null;
}

export function intInput(input: string): number | null {
  const v = input.trim();
  return /^\d+$/.test(v) ? Number(v) : null;
}
